import { z } from 'zod';

/**
 * URL-query boundary the Workspace search reads (`querySelector`) and writes
 * (`updateQueryAction`). `queryString.parse(..., { arrayFormat: 'bracket' })`
 * yields `string[]` for bracketed keys (`key[]=…`) and a scalar `string` for
 * plain keys (`key=…`). The tolerant `string → [string]` coercion guards a
 * hand-edited single-value URL so a `?stag=x` never breaks the array readers.
 *
 * `looseObject` (not the deprecated `.passthrough()`) forwards every unrelated
 * URL param — grid-filter state, `isSigned`, `linkPanelID`, the doc-type `tags`
 * filter, … — untouched through `.parse()`, so echoing a parsed query back
 * never drops keys this search does not own.
 */
const queryStringArray = z
  .union([z.string(), z.array(z.string())])
  .transform((v) => (Array.isArray(v) ? v : [v]));

export const workspaceSearchQuerySchema = z.looseObject({
  stag: queryStringArray.optional(), // free-text search term (the input box)
  text: queryStringArray.optional(), // #-tag / #-user chips
  date: queryStringArray.optional(), // [from, to] range, MM/DD/YYYY
  invoice_date: queryStringArray.optional(), // [from, to] range
  payment_date: queryStringArray.optional(), // [from, to] range
  label: z.string().optional(), // system user attribute (scalar; from saved Favorites)
  query: z.string().optional(), // raw Lucene expression (scalar; from saved Favorites)
});

export type WorkspaceSearchQuery = z.infer<typeof workspaceSearchQuerySchema>;
